import { jsPDF } from 'jspdf';
import { format } from 'date-fns';

interface LeaseData {
  studentName: string;
  studentId: string;
  roomNumber: string;
  buildingName: string;
  startDate: string;
  endDate: string;
  monthlyRent: number;
  depositAmount: number;
  signature: string;
  acceptanceDate: string;
  propertyId: string;
}

const addLogo = async (doc: jsPDF, propertyId: string): Promise<void> => {
  const storedLogos = localStorage.getItem('propertyLogos');
  if (!storedLogos) return;

  const propertyLogos = JSON.parse(storedLogos);
  const propertyLogo = propertyLogos[propertyId];
  
  if (propertyLogo?.logo) {
    try {
      await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          try {
            const maxWidth = propertyLogo.settings.leaseSize;
            const aspectRatio = img.width / img.height;
            const width = Math.min(maxWidth, img.width);
            const height = width / aspectRatio;

            const pageWidth = doc.internal.pageSize.getWidth();
            const xPos = (pageWidth - width) / 2;

            doc.addImage(img, 'PNG', xPos, 10, width, height);
            resolve(true);
          } catch (err) {
            reject(err);
          }
        };
        img.onerror = () => reject(new Error('Failed to load logo'));
        img.src = propertyLogo.logo;
      });
    } catch (error) {
      console.error('Error adding logo to PDF:', error);
    }
  }
};

export const generateLeasePDF = async (
  leaseContent: { coverPage: string; mainContent: string },
  leaseData: LeaseData
): Promise<Blob> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  const lineHeight = 4.5;

  // Add logo
  await addLogo(doc, leaseData.propertyId);

  // Add cover page
  doc.setFontSize(12);
  const coverLines = doc.splitTextToSize(leaseContent.coverPage, contentWidth);
  let yPos = 60;
  
  coverLines.forEach((line: string) => {
    if (yPos > pageHeight - margin) {
      doc.addPage();
      yPos = margin;
    }
    doc.text(line, margin, yPos);
    yPos += lineHeight;
  });

  // Add main content on new page
  doc.addPage();
  yPos = margin;

  // Add lease details
  doc.setFontSize(14);
  doc.text(`Lease Agreement`, margin, yPos);
  yPos += lineHeight * 2;

  doc.setFontSize(12);
  doc.text(`Student Name: ${leaseData.studentName}`, margin, yPos);
  yPos += lineHeight;
  doc.text(`Student ID: ${leaseData.studentId}`, margin, yPos);
  yPos += lineHeight;
  doc.text(`Room Number: ${leaseData.roomNumber}`, margin, yPos);
  yPos += lineHeight;
  doc.text(`Building: ${leaseData.buildingName}`, margin, yPos);
  yPos += lineHeight * 2;

  doc.text(`Lease Period:`, margin, yPos);
  yPos += lineHeight;
  doc.text(`Start Date: ${format(new Date(leaseData.startDate), 'MMMM d, yyyy')}`, margin + 10, yPos);
  yPos += lineHeight;
  doc.text(`End Date: ${format(new Date(leaseData.endDate), 'MMMM d, yyyy')}`, margin + 10, yPos);
  yPos += lineHeight * 2;

  doc.text(`Monthly Rent: $${leaseData.monthlyRent.toFixed(2)}`, margin, yPos);
  yPos += lineHeight;
  doc.text(`Security Deposit: $${leaseData.depositAmount.toFixed(2)}`, margin, yPos);
  yPos += lineHeight * 2;

  // Add main content
  const mainLines = doc.splitTextToSize(leaseContent.mainContent, contentWidth);
  mainLines.forEach((line: string) => {
    if (yPos > pageHeight - margin) {
      doc.addPage();
      yPos = margin;
    }
    doc.text(line, margin, yPos);
    yPos += lineHeight;
  });

  // Add signature section
  yPos += lineHeight * 2;
  if (yPos > pageHeight - margin * 3) {
    doc.addPage();
    yPos = margin;
  }

  doc.text('Student Signature:', margin, yPos);
  if (leaseData.signature) {
    yPos += 5;
    doc.addImage(leaseData.signature, 'PNG', margin, yPos, 50, 20);
  }
  yPos += 25;
  doc.text(`Date: ${format(new Date(leaseData.acceptanceDate), 'MMMM d, yyyy')}`, margin, yPos);

  return doc.output('blob');
};